import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { useEmulation } from '../contexts/EmulationContext'
import { useEffectiveAuth } from '../hooks/useEffectiveAuth'
import Navbar from './Navbar'
import ProfileModal from './ProfileModal'
import NotificationPreferencesModal from './NotificationPreferencesModal'
import MfaSettingsModal from './MfaSettingsModal'
import EmulationBanner from './EmulationBanner'
import MaintenanceBanner from './MaintenanceBanner'
import DemoBanner from './DemoBanner'
import TenantDeletionBanner from './TenantDeletionBanner'
import { useMaintenance } from '../contexts/MaintenanceContext'
import { creditsService } from '../services/credits'

const Layout = ({ children }) => {
  const { t } = useTranslation(['common', 'admin'])
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [profileModalOpen, setProfileModalOpen] = useState(false)
  const [notificationPrefsOpen, setNotificationPrefsOpen] = useState(false)
  const [mfaSettingsOpen, setMfaSettingsOpen] = useState(false)
  const location = useLocation()
  const { updateProfile } = useAuth()
  const { user, profile, hasCapability } = useEffectiveAuth()
  const { isEmulating } = useEmulation()
  const { isMaintenanceMode } = useMaintenance()

  // Get user role from token (if available) or from profile (API response)
  // SSO users may not have custom attributes in Cognito, so profile?.role is the fallback
  const userRole = user?.role || profile?.role

  // Fetch credit status for demo banner layout offset
  // Only fetch for tenant admins - regular users with capabilities don't need credit info
  const isSuperAdminUser = user?.isSuperAdmin === true
  const isPlatformAdminUser = user?.isPlatformAdmin === true
  const isEmulatingUser = !!isEmulating
  const isAdminUser = userRole === 'admin'
  const { data: creditStatus } = useQuery({
    queryKey: ['my-credit-status'],
    queryFn: () => creditsService.getMyCreditStatus(),
    staleTime: 60000,
    enabled: isAdminUser && !isSuperAdminUser && !isPlatformAdminUser && !isEmulatingUser,
  })
  const isDemoTenant = creditStatus?.type === 'demo' && isAdminUser && !isSuperAdminUser && !isPlatformAdminUser && !isEmulatingUser

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const isTenantAdmin = user?.isTenantAdmin || userRole === 'admin' || user?.isSuperAdmin || user?.isPlatformAdmin
  const isSuperAdmin = user?.isSuperAdmin === true
  const isPlatformAdmin = user?.isPlatformAdmin === true

  const navLinks = [
    { path: '/dashboard', label: t('navigation.dashboard'), icon: '📊' },
    { path: '/forms', label: t('navigation.forms'), icon: '📄' },
    // My Tasks and My Submissions only for users with tenant context
    ...(!isSuperAdmin ? [{ path: '/my-tasks', label: t('navigation.myTasks'), icon: '✓' }] : []),
    ...(!isSuperAdmin ? [{ path: '/my-submissions', label: t('navigation.mySubmissions'), icon: '📝' }] : []),
    // Tenant Admin or users with admin capabilities: consolidated Administration page
    ...((isTenantAdmin || ['manage_users', 'manage_groups', 'manage_imports', 'view_analytics', 'emulate_users'].some(cap => hasCapability(cap))) && !isSuperAdmin && !isPlatformAdmin
      ? [{ path: '/administration', label: t('navigation.administration'), icon: '⚙' }]
      : []),
    // Super/Platform Admin: Tenants page (contains per-tenant settings with users, usage, audit tabs).
    // Matches the /tenants route + TenantsPage guard, which both allow platform admins.
    ...(isSuperAdmin || isPlatformAdmin ? [{ path: '/tenants', label: t('navigation.tenants'), icon: '🏢' }] : []),
    // Super/Platform Admin: Platform Settings page (platform users + credits)
    ...(isSuperAdmin || isPlatformAdmin
      ? [{ path: '/platform-settings', label: t('navigation.platformSettings'), icon: '⚙' }]
      : []),
    // Super/Platform Admin: Support Tickets page
    ...(isSuperAdmin || isPlatformAdmin
      ? [{ path: '/support-tickets', label: t('navigation.supportTickets'), icon: '🎫' }]
      : []),
  ]

  return (
    <div className={`layout ${isEmulating ? 'layout--emulating' : ''} ${isMaintenanceMode ? 'layout--maintenance' : ''} ${isDemoTenant ? 'layout--demo' : ''}`}>
      {/* Maintenance banner - fixed at top when maintenance mode is active */}
      <MaintenanceBanner />

      {/* Emulation banner - fixed at top when viewing as another user */}
      <EmulationBanner />

      {/* Demo banner - fixed at top when tenant is on a demo/trial plan */}
      <DemoBanner />

      {/* Tenant deletion banner - shown when the active organization is pending deletion or deactivated */}
      <TenantDeletionBanner />

      <Navbar
        sidebarOpen={sidebarOpen}
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        onOpenProfile={() => setProfileModalOpen(true)}
        onOpenNotificationPrefs={() => setNotificationPrefsOpen(true)}
        onOpenMfaSettings={() => setMfaSettingsOpen(true)}
      />

      <div className="layout-body">
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <nav className="sidebar-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${isActive(link.path) ? 'nav-link-active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="nav-icon">{link.icon}</span>
                <span className="nav-label">{link.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          <div className="content-wrapper">
            {children}
          </div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        profile={profile}
        onSave={updateProfile}
      />

      {/* Notification Preferences Modal */}
      <NotificationPreferencesModal
        isOpen={notificationPrefsOpen}
        onClose={() => setNotificationPrefsOpen(false)}
      />

      {/* MFA Settings Modal */}
      <MfaSettingsModal
        isOpen={mfaSettingsOpen}
        onClose={() => setMfaSettingsOpen(false)}
      />
    </div>
  )
}

export default Layout
